import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import {
  Box, Typography, Button, Card, CardContent, Grid, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Paper
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';

const ActiveUsageScreen = ({ children }) => {
  const [activeUsages, setActiveUsages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);
  const navigate = useNavigate();

  // Real-time listener for active instrument usages
  // Replaced polling (getDocs + setInterval) with onSnapshot for better performance and reliability
  useEffect(() => {
    if (!auth.currentUser) {
      setActiveUsages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    const usageQuery = query(
      collection(db, 'active_instrument_usage'),
      where('userId', '==', auth.currentUser.uid)
    );

    // Use onSnapshot for real-time updates - single persistent connection
    // This prevents WebChannel 404 errors by using one connection instead of polling
    const unsubscribe = onSnapshot(
      usageQuery,
      async (snapshot) => {
        if (snapshot.empty) {
          setActiveUsages([]);
          setLoading(false);
          return;
        }

        // Fetch details for each active usage
        const usageData = await Promise.all(
          snapshot.docs.map(async (usageDoc) => {
            const usageData = usageDoc.data();
            
            // Get alat details
            let alatName = 'Unknown';
            let alatType = 'instrumen';
            
            try {
              // Try to get from instrument collection
              const instrumentDoc = await getDoc(doc(db, 'instrumen', usageData.alatId));
              if (instrumentDoc.exists()) {
                alatName = instrumentDoc.data().nama || 'Unknown';
                alatType = 'instrumen';
              } else {
                // Try alat gelas - use direct doc reference
                try {
                  const alatGelasDoc = await getDoc(doc(db, 'alat_gelas', usageData.alatId));
                  if (alatGelasDoc.exists()) {
                    alatName = alatGelasDoc.data().nama || 'Unknown';
                    alatType = 'alat_gelas';
                  }
                } catch (err) {
                  console.error('Error fetching alat gelas:', err);
                }
              }
            } catch (err) {
              console.error('Error fetching alat details:', err);
            }

            // Get logbook entry if exists - check both field names (logbookEntryId is used in QRScanPage)
            let logbookData = null;
            const logbookId = usageData.logbookEntryId || usageData.logbookId;
            if (logbookId) {
              try {
                const logbookDoc = await getDoc(doc(db, 'logbook_instrumen', logbookId));
                if (logbookDoc.exists()) {
                  logbookData = { id: logbookDoc.id, ...logbookDoc.data() };
                }
              } catch (err) {
                console.error('Error fetching logbook:', err);
              }
            }

            // Handle startTime - check both field names and formats
            let startTime = usageData.startTime || usageData.timestampMulai;
            // If startTime is a Date object, convert to Timestamp for consistency
            if (startTime && startTime instanceof Date) {
              startTime = Timestamp.fromDate(startTime);
            }

            return {
              id: usageDoc.id,
              alatId: usageData.alatId,
              alatName,
              alatType,
              startTime: startTime,
              logbookId: logbookId,
              logbookData,
              usageData
            };
          })
        );

        setActiveUsages(usageData);
        setLoading(false);
      },
      (error) => {
        // Handle listener errors (including WebChannel 404 errors)
        const isOfflineError = 
          error.code === 'unavailable' || 
          error.code === 'failed-precondition' ||
          error.code === 'cancelled' ||
          error.message?.toLowerCase().includes('offline') ||
          error.message?.toLowerCase().includes('client is offline') ||
          error.message?.toLowerCase().includes('network') ||
          error.message?.toLowerCase().includes('listen') ||
          error.message?.includes('404');
        
        if (!isOfflineError) {
          console.error('Error in active usage listener:', error);
        }
        // Keep existing usages on transient errors (like WebChannel 404)
        // The listener will automatically reconnect
        setLoading(false);
      }
    );

    // Cleanup listener on unmount or user change
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser?.uid]); // Re-run when user changes

  const handleCompleteUsage = async (usage) => {
    setCompletingId(usage.id);
    try {
      // Navigate to logbook completion page with alatId
      navigate(`/scan-alat?alatId=${usage.alatId}&complete=true`);
    } catch (error) {
      console.error('Error completing usage:', error);
    } finally {
      setCompletingId(null);
    }
  };

  const handleScanAnother = () => {
    navigate('/scan-alat');
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('id-ID');
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString('id-ID');
    }
    return 'N/A';
  };

  const calculateDuration = (startTime) => {
    if (!startTime) return 'N/A';
    let start;
    if (startTime.toDate) {
      start = startTime.toDate();
    } else if (startTime.seconds) {
      start = new Date(startTime.seconds * 1000);
    } else {
      return 'N/A';
    }
    
    const now = new Date();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    
    if (diffHours > 0) {
      return `${diffHours} jam ${remainingMins} menit`;
    }
    return `${diffMins} menit`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (activeUsages.length === 0) {
    // No active usage, allow normal navigation
    return children || <Outlet />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="warning" sx={{ mb: 3 }}>
        Anda sedang menggunakan {activeUsages.length} alat. Silakan selesaikan penggunaan sebelum melanjutkan.
      </Alert>

      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Alat yang Sedang Digunakan ({activeUsages.length})
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Nama Alat</strong></TableCell>
              <TableCell><strong>Waktu Mulai</strong></TableCell>
              <TableCell><strong>Durasi</strong></TableCell>
              <TableCell align="center"><strong>Aksi</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeUsages.map((usage) => (
              <TableRow key={usage.id}>
                <TableCell>{usage.alatName}</TableCell>
                <TableCell>{formatTime(usage.startTime)}</TableCell>
                <TableCell>{calculateDuration(usage.startTime)}</TableCell>
                <TableCell align="center">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleCompleteUsage(usage)}
                    disabled={completingId === usage.id}
                  >
                    {completingId === usage.id ? 'Memproses...' : 'Selesaikan'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        justifyContent: 'center', 
        flexWrap: 'wrap', 
        mt: 3,
        mb: 3,
        p: 2,
        backgroundColor: '#f5f5f5',
        borderRadius: 2
      }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<HomeIcon />}
          onClick={() => {
            console.log('Navigating to dashboard from ActiveUsageScreen');
            navigate('/dashboard', { replace: true });
          }}
          size="large"
          sx={{ 
            minWidth: '220px',
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          Kembali ke Dashboard
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<QrCodeScannerIcon />}
          onClick={() => {
            console.log('Navigating to scan page');
            navigate('/scan-alat');
          }}
          size="large"
          sx={{ 
            minWidth: '220px',
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          Scan Alat Lain
        </Button>
      </Box>
    </Box>
  );
};

export default ActiveUsageScreen;

